import React, {FC} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CalendarTeammateAccessRolesEnum} from '../../../models/api/calendarModel';
import {DefaultTeammateAccessRolesEnum} from '../../../models/api/companyPreferencesModel';

/*
 * Props.
 */

interface CalendarTeammateAccessRoleDropdownBoxProps {
  defaultTeammateAccessRole: DefaultTeammateAccessRolesEnum;
  teammateAccessRole: CalendarTeammateAccessRolesEnum;
  onTeammateAccessRoleChange: HandlerOf<CalendarTeammateAccessRolesEnum>;
}

/*
 * Component.
 */

export const CalendarTeammateAccessRoleDropdownBox: FC<CalendarTeammateAccessRoleDropdownBoxProps> = (
  props,
) => {
  const currentAccessRole = findAccessRole(props.teammateAccessRole, props.defaultTeammateAccessRole);

  return (
    <DropdownBoxCoordinator
      label={<Trans id="edit-calendar-teammate-access">Teammate access</Trans>}
      value={defaultTeammateAccessRoleToText[currentAccessRole]}
      renderDropdown={(popoverProps) => renderDropdown(popoverProps, props, currentAccessRole)}
    />
  );
};

function findAccessRole(
  calendarTeammateAccessRole: CalendarTeammateAccessRolesEnum,
  defaultTeammateAccessRole: DefaultTeammateAccessRolesEnum,
) {
  const accessRoleMapping = {
    [CalendarTeammateAccessRolesEnum.NONE]: DefaultTeammateAccessRolesEnum.NONE,
    [CalendarTeammateAccessRolesEnum.FREE_BUSY]: DefaultTeammateAccessRolesEnum.FREE_BUSY,
    [CalendarTeammateAccessRolesEnum.READER]: DefaultTeammateAccessRolesEnum.READER,
    [CalendarTeammateAccessRolesEnum.INHERIT]: defaultTeammateAccessRole,
  };

  return accessRoleMapping[calendarTeammateAccessRole];
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: CalendarTeammateAccessRoleDropdownBoxProps,
  currentAccessRole: DefaultTeammateAccessRolesEnum,
) {
  const accessRoles = [
    DefaultTeammateAccessRolesEnum.READER,
    DefaultTeammateAccessRolesEnum.FREE_BUSY,
    DefaultTeammateAccessRolesEnum.NONE,
  ];

  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        {accessRoles.map((r) => renderItem(props, r, r === currentAccessRole))}
      </DropdownList>
    </Dropdown>
  );
}

function renderItem(
  props: CalendarTeammateAccessRoleDropdownBoxProps,
  accessRole: DefaultTeammateAccessRolesEnum,
  isChecked: boolean,
) {
  return (
    <DropdownItem
      key={accessRole}
      isChecked={isChecked}
      onSelect={() =>
        props.onTeammateAccessRoleChange(defaultTeammateAccessRoleToCalendarTeammateAccessRole[accessRole])
      }
    >
      <DropdownItemContent isChecked={isChecked}>
        {defaultTeammateAccessRoleToText[accessRole]}
      </DropdownItemContent>
    </DropdownItem>
  );
}

const defaultTeammateAccessRoleToCalendarTeammateAccessRole = {
  [DefaultTeammateAccessRolesEnum.NONE]: CalendarTeammateAccessRolesEnum.NONE,
  [DefaultTeammateAccessRolesEnum.FREE_BUSY]: CalendarTeammateAccessRolesEnum.FREE_BUSY,
  [DefaultTeammateAccessRolesEnum.READER]: CalendarTeammateAccessRolesEnum.READER,
};

const defaultTeammateAccessRoleToText = {
  [DefaultTeammateAccessRolesEnum.NONE]: <Trans id="calendar-access-role-none">No access</Trans>,
  [DefaultTeammateAccessRolesEnum.FREE_BUSY]: (
    <Trans id="calendar-access-role-freebusy">Freebusy (hide details)</Trans>
  ),
  [DefaultTeammateAccessRolesEnum.READER]: <Trans id="calendar-access-role-reader">All event details</Trans>,
};
